/* eslint-disable @typescript-eslint/naming-convention */
import { inject, Injectable } from '@angular/core';

import { StorageService } from './storage/storage.service';

export interface AuthTokenResponse {
    access_token: string;
    id_token?: string;
    expires_in: number;
}

const ACCESS_TOKEN_KEY = 'access_token';
const TOKEN_EXPIRES_AT_KEY = 'access_token_expires_at';

// Treat the token as expired slightly early to avoid racing the server clock
const EXPIRY_LEEWAY_MS = 60 * 1000;

/**
 * Holds the Auth0 access token used to call BAPI.
 *
 * Tokens are only needed while online: when offline, data is read from the
 * local cache, so an expired access token never blocks the app.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
    private storage = inject(StorageService);

    async setTokens(tokens: AuthTokenResponse): Promise<void> {
        const expiresAt = Date.now() + tokens.expires_in * 1000;

        await Promise.all([
            this.storage.set(ACCESS_TOKEN_KEY, tokens.access_token),
            this.storage.set(TOKEN_EXPIRES_AT_KEY, String(expiresAt)),
        ]);
    }

    async clear(): Promise<void> {
        await Promise.all([
            this.storage.remove(ACCESS_TOKEN_KEY),
            this.storage.remove(TOKEN_EXPIRES_AT_KEY),
        ]);
    }

    /**
     * Returns the access token, or null when there is no session or it has expired.
     */
    async getAccessToken(): Promise<string | null> {
        const [token, expiresAt] = await Promise.all([
            this.storage.get(ACCESS_TOKEN_KEY),
            this.storage.get(TOKEN_EXPIRES_AT_KEY).then((exp) => (exp ? +exp : 0)),
        ]);

        return token && Date.now() < expiresAt - EXPIRY_LEEWAY_MS ? token : null;
    }

    /**
     * Whether a token was stored at login. Works offline: it does not check expiry.
     */
    async hasTokens(): Promise<boolean> {
        return !!(await this.storage.get(ACCESS_TOKEN_KEY));
    }

    async getAuthHeaders(): Promise<Record<string, string>> {
        const token = await this.getAccessToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
}
